import * as React from "react";

import { useForkRef } from "../forms/useForkRef";
import "./interactable-card.css";

/**
 * InteractableCard — a card you can click, replacing `@salt-ds/core`'s behind `metric-card.tsx`.
 *
 * ── WHY A DIV WITH role="button" AND NOT A <button> ───────────────────────────────────────
 *
 * A metric card contains headings and links. `<button>` forbids interactive descendants, and a
 * browser will not render a heading inside one the way the design asks. The vendor's answer —
 * which this port keeps — is a `div` with `role="button"`, `tabIndex` and hand-written
 * Enter/Space handling, because a div gets NONE of that for free.
 *
 * That hand-written keyboard path is the whole reason this component is not in `Card.tsx` with
 * the display surfaces: get it wrong and the card is simply unreachable without a mouse.
 *
 * ── THE `active` FLAG IS A PRESSED LOOK, NOT STATE ────────────────────────────────────────
 *
 * `:active` styles a mouse press. A keyboard press fires no `:active`, so without this the card
 * looks inert while a keyboard user activates it. `active` is set on key-down and cleared on
 * key-up or blur, and it paints `saltInteractableCard-active`, which the stylesheet groups with
 * `:active`. It is deliberately NOT part of `onClick`'s contract.
 *
 * ── ONE DELIBERATE DEVIATION: A DISABLED CARD DOES NOTHING ────────────────────────────────
 *
 * The vendor wires the div's `onClick` to a handler that is NOT gated on `disabled`, and its
 * key handler is ungated too, so a disabled vendor card still fires the caller's `onClick` on
 * both mouse and Enter. Its stylesheet sets `pointer-events: none` only on NESTED divs, so a
 * click on the card's own padding gets through. MEASURED in Chrome, not inferred: a disabled
 * vendor card given one click and one Enter fired the caller's `onClick` TWICE. Ours fired
 * zero times. Every other measurement in that run — 21 computed properties plus the accent
 * bar's `::after` box, across 10 configurations — was identical.
 *
 * This port gates both. That is a real behaviour change and it is stated here rather than
 * buried: "disabled" that still fires is a defect, not a convention worth preserving, and no
 * call site in this app passes `disabled`, so nothing here changes shape because of it.
 *
 * ── A VENDOR ASYMMETRY THIS PORT KEEPS ────────────────────────────────────────────────────
 *
 * On a mouse click the vendor runs its own handler, which fires `onChange` and then `onClick`.
 * On Enter/Space it calls `onClick` DIRECTLY from the key handler, so `onChange` never fires.
 * For a card in a selection group that is a real defect — keyboard selection does not select —
 * but this app uses one standalone card (`metric-card.tsx`), where the two paths are equivalent
 * because only `onClick` is passed. Matching the vendor is the safe choice; changing it here
 * would be an undiscussed behaviour change hiding inside a port. Groups are not ported: nothing
 * in this app mounts one, and porting the group without the fix would ship the defect.
 */
export type InteractableCardVariant = "primary" | "secondary" | "tertiary";
export type InteractableCardAccent = "top" | "right" | "bottom" | "left";

export interface InteractableCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  accent?: InteractableCardAccent;
  /** The vendor's older name for `accent`; `accent` wins when both are given, as it did there. */
  accentPlacement?: InteractableCardAccent;
  disabled?: boolean;
  variant?: InteractableCardVariant;
  value?: string;
  onChange?: (event: React.SyntheticEvent) => void;
}

function capitalize(value: string): string {
  return value ? value[0].toUpperCase() + value.slice(1) : value;
}

export const InteractableCard = React.forwardRef<HTMLDivElement, InteractableCardProps>(
  function InteractableCard(
    {
      accent,
      accentPlacement,
      children,
      className,
      disabled,
      onBlur,
      onChange,
      onClick,
      onKeyDown,
      onKeyUp,
      style,
      value,
      variant = "primary",
      ...rest
    },
    ref,
  ) {
    const [active, setActive] = React.useState(false);
    const cardRef = React.useRef<HTMLDivElement>(null);
    const handleRef = useForkRef(ref, cardRef);

    const accentValue = accent ?? accentPlacement;

    const classes = ["saltInteractableCard", `saltInteractableCard-${variant}`];
    if (accentValue) {
      // BOTH classes: `-accent` draws the bar at all, `-accentTop` places it. One alone is invisible.
      classes.push("saltInteractableCard-accent", `saltInteractableCard-accent${capitalize(accentValue)}`);
    }
    if (active) classes.push("saltInteractableCard-active");
    if (disabled) classes.push("saltInteractableCard-disabled");
    if (className) classes.push(className);

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      setActive(true);
      onChange?.(event);
      onClick?.(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        /*
         * `preventDefault` matters for Space specifically: on a focusable div the browser
         * scrolls the page. Activating a card and scrolling away from it at the same time is
         * the kind of thing that reads as a broken page rather than a missing preventDefault.
         */
        event.preventDefault();
        setActive(true);
        onClick?.(event as unknown as React.MouseEvent<HTMLDivElement>);
      }
      onKeyDown?.(event);
    };

    const handleKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
      setActive(false);
      onKeyUp?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
      // Focus can leave mid-press (an Enter that opens a dialog). Without this the card stays
      // painted as pressed forever, because the matching key-up lands somewhere else.
      setActive(false);
      onBlur?.(event);
    };

    return (
      <div
        aria-disabled={disabled || undefined}
        className={classes.join(" ")}
        data-value={value}
        ref={handleRef}
        role="button"
        style={style}
        // -1 rather than removing it: a disabled card stays reachable programmatically and
        // keeps its place in the DOM order, it just leaves the tab sequence.
        tabIndex={disabled ? -1 : 0}
        {...rest}
        onBlur={handleBlur}
        onClick={disabled ? undefined : handleClick}
        onKeyDown={disabled ? undefined : handleKeyDown}
        onKeyUp={handleKeyUp}
      >
        {children}
      </div>
    );
  },
);
